import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FitnessProgress } from '@/components/ui/progress'
import {
  TrendingUp,
  Scale,
  Ruler,
  Target,
  Calendar,
  Plus,
  Award,
  Zap,
  Activity
} from 'lucide-react'

const ProgressTracker = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week')

  // Mock data - in a real app, this would come from your store
  const progressData = {
    weight: { current: 75, goal: 70, unit: 'kg', change: -2.5 },
    bodyFat: { current: 18, goal: 15, unit: '%', change: -1.2 },
    muscle: { current: 42, goal: 45, unit: 'kg', change: +0.8 },
    bmi: { current: 23.4, goal: 22.0, unit: '', change: -0.6 }
  }

  const achievements = [
    { id: 1, title: '7-Day Streak', description: 'Completed workouts for 7 days straight', icon: '🔥', unlocked: true },
    { id: 2, title: 'First 5K', description: 'Ran your first 5 kilometers', icon: '🏃', unlocked: true },
    { id: 3, title: 'Nutrition Master', description: 'Logged meals for 30 days', icon: '🥗', unlocked: false },
    { id: 4, title: 'Strength Warrior', description: 'Lifted 1000kg total volume', icon: '💪', unlocked: false }
  ]

  const weeklyStats = [
    { day: 'Mon', workouts: 1, calories: 2100 },
    { day: 'Tue', workouts: 0, calories: 1950 },
    { day: 'Wed', workouts: 1, calories: 2200 },
    { day: 'Thu', workouts: 1, calories: 2050 },
    { day: 'Fri', workouts: 0, calories: 1900 },
    { day: 'Sat', workouts: 1, calories: 2300 },
    { day: 'Sun', workouts: 1, calories: 2150 }
  ]

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-heading">Progress Tracker</h1>
          <p className="app-page-subheading">Monitor your fitness journey and celebrate achievements</p>
        </div>
        <Button variant="fitness">
          <Plus className="h-4 w-4 mr-2" />
          Log Progress
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-6">
        {Object.entries(progressData).map(([key, data], index) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="rounded-lg bg-slate-100 p-2">
                    {key === 'weight' && <Scale className="h-5 w-5 text-fitness-primary" />}
                    {key === 'bodyFat' && <Target className="h-5 w-5 text-fitness-primary" />}
                    {key === 'muscle' && <Activity className="h-5 w-5 text-fitness-primary" />}
                    {key === 'bmi' && <TrendingUp className="h-5 w-5 text-fitness-primary" />}
                  </div>
                  <div className={`rounded-full px-2 py-1 text-sm font-medium ${
                    data.change > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                  }`}>
                    {data.change > 0 ? '+' : ''}{data.change}{data.unit}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-semibold text-slate-900">
                      {data.current}
                    </span>
                    <span className="text-sm text-slate-400">{data.unit}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Goal: {data.goal}{data.unit}
                  </div>
                  <div className="text-xs capitalize text-slate-400">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Progress Chart */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-fitness-primary" />
                <span>Progress Overview</span>
              </CardTitle>
              <div className="flex space-x-2">
                {['week', 'month', '3months'].map((period) => (
                  <Button
                    key={period}
                    variant={selectedPeriod === period ? "selected" : "outline"}
                    size="sm"
                    onClick={() => setSelectedPeriod(period)}
                  >
                    {period === '3months' ? '3M' : period === 'month' ? '1M' : '1W'}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {/* Mock chart area */}
              <div className="flex h-64 items-center justify-center rounded-lg bg-slate-50">
                <div className="text-center text-slate-400">
                  <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Progress chart would be displayed here</p>
                  <p className="text-sm">Integration with charting library needed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Activity */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-green-600" />
                <span>This Week</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {weeklyStats.map((day, index) => (
                  <div key={day.day} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-sm font-medium">
                        {day.day.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{day.day}</div>
                        <div className="text-xs text-slate-400">{day.calories} cal</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {day.workouts > 0 ? (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      ) : (
                        <div className="h-2 w-2 rounded-full bg-slate-300"></div>
                      )}
                      <span className="text-sm text-slate-500">{day.workouts}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Achievements */}
      <div>
        <h2 className="mb-4 text-xl font-semibold text-slate-900">Achievements</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {achievements.map((achievement, index) => (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={achievement.unlocked ? 'ring-2 ring-fitness-primary/10' : 'opacity-60'}>
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">{achievement.icon}</div>
                  <h3 className="mb-2 font-semibold text-slate-900">{achievement.title}</h3>
                  <p className="mb-4 text-sm text-slate-500">{achievement.description}</p>
                  {achievement.unlocked ? (
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                      <Award className="h-4 w-4 mr-1" />
                      Unlocked
                    </div>
                  ) : (
                    <div className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-500">
                      <Target className="h-4 w-4 mr-1" />
                      Locked
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Goals Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-purple-600" />
            <span>Goal Progress</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <FitnessProgress
              value={75}
              label="Weight Loss Goal"
              showPercentage
              variant="calories"
            />
            <div className="mt-1 flex justify-between text-sm text-slate-500">
              <span>Current: 75kg</span>
              <span>Target: 70kg</span>
            </div>
          </div>
          
          <div>
            <FitnessProgress
              value={60}
              label="Muscle Gain Goal"
              showPercentage
              variant="protein"
            />
            <div className="mt-1 flex justify-between text-sm text-slate-500">
              <span>Current: 42kg</span>
              <span>Target: 45kg</span>
            </div>
          </div>
          
          <div>
            <FitnessProgress
              value={85}
              label="Workout Consistency"
              showPercentage
              variant="workout"
            />
            <div className="mt-1 flex justify-between text-sm text-slate-500">
              <span>17/20 workouts this month</span>
              <span>Target: 20 workouts</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ProgressTracker
